import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import HammerIcon from "@/assets/dark_gradient/hammer.svg?react";
import RightArrowIcon from "@/assets/light_gradient/right_arrow.svg?react";
import Button from "@/components/Common/Button";
import Modal from "@/components/Common/Modal";
import {Tooltip} from "@/components/Common/Tooltip";
import DisciplineContent from "@/components/discipline/DisciplineContent";
import DisciplineHistory from "@/components/discipline/DisciplineHistory";
import DisciplineIndicators from "@/components/discipline/DisciplineIndicators";
import DisciplineMark from "@/components/discipline/DisciplineMark";
import DisciplineTabs from "@/components/discipline/DisciplineTabs";
import FlaggedCampaign from "@/components/discipline/FlaggedCampaign";
import ModalButtonRow from "@/components/discipline/ModalButtonRow";
import InfluencerMiniProfile from "@/components/Influencer/InfluencerMiniProfile";
import type {PunishmentSerializer} from "@/generated/DjangoTypes";
import {useDiscipline} from "@/hooks/useDiscipline";
import {Can} from "@/permissions";
import {useManagementCampaign, useRequiredCampaignContent} from "@/store/campaignManagement";
import {disciplinaryModalAtom, disciplineTabAtom, isDisciplinaryModalOpenAtom} from "@/store/jotaiAtoms";
import {isDisciplineActive} from "@/utils/disciplineUtils";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {useEffect, type FC} from "react";
import {useTranslation} from "react-i18next";

const DisciplinaryModal: FC = () => {
  const {activeCampaignsCount, getDisciplinaryModalPreviousLocation, setDisciplineByTab} = useDiscipline();
  const {t} = useTranslation("influencer_discipline");
  const setModalOpen = useSetAtom(isDisciplinaryModalOpenAtom);
  const [disciplinaryData, setDisciplinaryData] = useAtom(disciplinaryModalAtom);
  const disciplineTab = useAtomValue(disciplineTabAtom);
  const {lastDiscipline} = disciplinaryData;

  const campaign = useManagementCampaign();
  const requiredContent = useRequiredCampaignContent();

  const user: Record<string, PunishmentSerializer> = {};

  if (disciplinaryData?.instagramDiscipline !== undefined && disciplinaryData.instagramDiscipline !== null) {
    user.instagram_unverified = disciplinaryData.instagramDiscipline;
  }

  if (disciplinaryData?.lastDiscipline !== undefined && disciplinaryData.lastDiscipline !== null) {
    user.last_punishment = disciplinaryData.lastDiscipline;
  }

  if (disciplinaryData?.tiktokDiscipline !== undefined && disciplinaryData.tiktokDiscipline !== null) {
    user.tiktok_unverified = disciplinaryData.tiktokDiscipline;
  }

  if (disciplinaryData?.ugcDiscipline !== undefined && disciplinaryData.ugcDiscipline !== null) {
    user.ugc_unverified = disciplinaryData.ugcDiscipline;
  }

  const userWithDisciplines = user;

  useEffect(() => {
    setDisciplineByTab(disciplineTab);
  }, [disciplineTab, setDisciplineByTab]);

  if (!disciplinaryData) {
    return null;
  }

  return (
    <Can I="create" a="DisciplinaryOperation">
      <Modal
        backgroundClassName="max-w-207.5 bg-white"
        className="p-0 pt-5"
        customBottomRow={
          <ModalButtonRow onBack={() => setModalOpen(false)} onSuccessAction={() => setModalOpen(false)} />
        }
        customTitleRow={
          <div className="flex flex-col items-start gap-3 pt-5 pb-4 pl-5 sm:flex-row sm:items-center">
            <Button buttonStyle="White" className="h-fit gap-2 px-4 py-2" onClick={() => setModalOpen(false)}>
              <RightChevronIcon className="text-primary-dark-solid size-3 rotate-180" />
              <span className="text-sm font-medium">{getDisciplinaryModalPreviousLocation()}</span>
            </Button>
            <div className="flex items-center gap-2">
              <HammerIcon className="size-4" />
              <span className="text-neutral-black text-xl font-semibold">{t("disciplinary_action")}</span>
            </div>
          </div>
        }
        onClose={() => setModalOpen(false)}>
        <div className="flex flex-col gap-6 px-5">
          <div>
            <div className="flex overflow-x-auto">
              <div className="text-neutral-black flex flex-col gap-1 text-sm">
                <span>{t("influencer")}</span>
                <div className="border-neutral-white-dark flex min-w-[33%] items-center gap-2 border-y py-3">
                  <DisciplineIndicators
                    disabled
                    influencer={{
                      user: {
                        ...{
                          ...userWithDisciplines,
                          active_recently: false,
                          active_recently_last_two_months: false,
                          currency: "CHF",
                          tiktok_verified: !userWithDisciplines.tiktok_unverified,
                          ugc_verified: !userWithDisciplines.ugc_unverified,
                        },
                        id: disciplinaryData.userId!,
                      },
                    }}
                    showMostImpactfulOnly
                  />
                  {disciplinaryData.influencer.id && (
                    <InfluencerMiniProfile
                      id={disciplinaryData.influencer.id}
                      firstName={disciplinaryData.influencer.firstName}
                      image={disciplinaryData.influencer.image}
                      username={disciplinaryData.influencer.username}
                      usernameClassName="text-primary-dark-solid"
                    />
                  )}
                </div>
              </div>
              <div className="text-neutral-black flex w-full flex-col gap-1 text-sm">
                <span className="pl-3 text-nowrap">{t("active_campaigns")}</span>
                <div className="border-neutral-white-dark flex items-center justify-between gap-2 border-y py-3 pl-3">
                  <span className="text-sm font-semibold">{activeCampaignsCount ?? 0}</span>
                  <Button
                    data-tooltip-id={`disciplinary-${disciplinaryData.influencer.id}-view-applicant`}
                    onClick={() => window.open(`/influencer/${disciplinaryData.influencer.id}/`, "_blank")}
                    buttonStyle="White"
                    className="size-8">
                    <RightArrowIcon className="size-2.5 shrink-0" />
                  </Button>
                  <Tooltip
                    id={`disciplinary-${disciplinaryData.influencer.id}-view-applicant`}
                    variant="dark"
                    place="bottom"
                    className="z-50">
                    {t("view_applicant_profile")}
                  </Tooltip>
                </div>
              </div>
            </div>
            {disciplinaryData.userId && <DisciplineHistory userId={disciplinaryData.userId} />}
          </div>
          {disciplinaryData.campaignApplication && (
            <FlaggedCampaign
              data={{
                id: campaign?.id,
                campaign: {
                  id: campaign.id,
                  number_of_feedposts: requiredContent.instagram.posts,
                  number_of_reels: requiredContent.instagram.reels,
                  number_of_stories: requiredContent.instagram.stories,
                  number_of_ugc: requiredContent.ugc,
                  picture640: campaign?.picture640,
                  tiktok_number_of_medias: requiredContent.tiktok,
                  title: campaign.title,
                },
                instagram_medias: [],
                posts_done: disciplinaryData.campaignApplication.posts_done,
                reels_done: disciplinaryData.campaignApplication.reels_done,
                stories_done: disciplinaryData.campaignApplication.stories_done,
                tiktok_number_of_medias_done:
                  disciplinaryData.campaignApplication.tiktok?.length ??
                  disciplinaryData.campaignApplication?.tiktok_number_of_medias_done,
                tiktok_medias: [],
                ugc_done: disciplinaryData.campaignApplication.ugc_done,
              }}
              onClose={() => {
                setDisciplinaryData(draft => {
                  draft.campaignApplication = undefined;
                });
              }}
            />
          )}
        </div>
        <DisciplineTabs
          tabs={[
            {
              atomValue: "UNVERIFY",
              title: t("unverify_influencer"),
              Icon: <DisciplineMark className="size-3.5" type="GENERAL_UNVERIFIED" variant="outline" />,
              isActive: !!(
                disciplinaryData.instagramDiscipline ||
                disciplinaryData.tiktokDiscipline ||
                disciplinaryData.ugcDiscipline
              ),
            },
            {
              atomValue: "PROBATION",
              title: t("probation"),
              Icon: <DisciplineMark className="size-3.5" type="PROBATION" variant="outline" />,
              isActive: lastDiscipline?.punishment_type === "PROBATION" && isDisciplineActive(lastDiscipline),
            },
            {
              atomValue: "TIMED_BAN",
              title: t("timed_ban"),
              Icon: <DisciplineMark className="size-3.5" type="TIMED_BAN" variant="outline" />,
              isActive: lastDiscipline?.punishment_type === "TIMED_BAN" && isDisciplineActive(lastDiscipline),
            },
            {
              atomValue: "PERMANENT_BAN",
              title: `${t("ban")}`,
              Icon: <DisciplineMark className="size-3.5" type="PERMANENT_BAN" variant="outline" />,
              isActive: lastDiscipline?.punishment_type === "PERMANENT_BAN" && isDisciplineActive(lastDiscipline),
            },
          ]}>
          {disciplineTab === "UNVERIFY" && (
            <DisciplineContent
              durationTitle={t("wait_time_to_reverify")}
              showTypeSwitcher
              toggleTitle={t("allow_reverification")}
              showToggle={disciplinaryData.selectedUnverificationType !== "ugc"}
              showDays={disciplinaryData.selectedUnverificationType !== "ugc"}
            />
          )}
          {disciplineTab === "PROBATION" && (
            <DisciplineContent durationTitle={t("probation_time")} toggleTitle={t("timed_probation")} />
          )}
          {disciplineTab === "TIMED_BAN" && <DisciplineContent durationTitle={t("ban_time")} />}
          {disciplineTab === "PERMANENT_BAN" && <DisciplineContent showDays={false} />}
        </DisciplineTabs>
      </Modal>
    </Can>
  );
};

export default DisciplinaryModal;
